import React, { useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

function AddRoomType() {
  const [roomType, setRoomType] = useState('');
  const [description, setDescription] = useState('');
  const [maxOccupancy, setMaxOccupancy] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [isRefundable, setIsRefundable] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [images, setImages] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [selectedServiceCategories, setSelectedServiceCategories] = useState([]);
  const [amenitiesList, setAmenitiesList] = useState([]);
  const [serviceCategoriesList, setServiceCategoriesList] = useState([]);
  const [basePrice, setBasePrice] = useState('');

  // Fetch room types data which includes both amenities and service categories
  useEffect(() => {
    fetch(`${BQ_BASE_URL}/bq/api/admin-roomtypes/`)
      .then((response) => response.json())
      .then((data) => {
        // Use the all_amenities and all_roomservices from the API response
        setAmenitiesList(data.all_amenities || []);
        setServiceCategoriesList(data.all_roomservices || []);
      })
      .catch((error) => {
        console.error('Error fetching room types data:', error);
        toast.error('Failed to fetch data. Please try again.');
      });
  }, []);

  const onDrop = (acceptedFiles) => {
    setImages([...images, ...acceptedFiles]);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: 'image/*',
    multiple: true,
  });

  const handleCheckboxChange = (list, setList, item) => {
    setList((prev) => (prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]));
  };

  const toggleRefundable = () => {
    setIsRefundable(!isRefundable);
  };

  const toggleActive = () => {
    setIsActive(!isActive);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prepare the form data
    const formData = new FormData();

    // Prepare roomtype_data as JSON string
    const roomtypeData = {
      propertyid: 1, // Assuming propertyid is fixed or fetched from somewhere
      roomtypename: roomType,
      description: description,
      baseprice: parseFloat(basePrice),
      max_occupancy: parseInt(maxOccupancy),
      deposit_amount: parseFloat(depositAmount),
      is_refundable: isRefundable,
      is_active: isActive,
      amenity_category_ids: selectedAmenities,
      roomservice_category_ids: selectedServiceCategories,
    };

    // Append roomtype_data as JSON string
    formData.append('roomtype_data', JSON.stringify(roomtypeData));

    // Append images
    images.forEach((image) => {
      formData.append('images', image);
    });

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/admin-roomtypes/`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const result = await response.json();
      console.log('Room type created successfully:', result);
      toast.success('Room type created successfully!');
      // Reset the form
      setRoomType('');
      setDescription('');
      setBasePrice('');
      setDepositAmount('');
      setMaxOccupancy('');
      setIsRefundable(true);
      setIsActive(true);
      setImages([]);
      setSelectedAmenities([]);
      setSelectedServiceCategories([]);
    } catch (error) {
      console.error('Error creating room type:', error);
      toast.error('Failed to create room type. Please try again.');
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen flex flex-col">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="flex items-center justify-between mb-2 mt-4">
        <Link
          to="/dashboard/mdm/room-management/rooms"
          className="flex items-center text-gray-400 hover:text-black"
        >
          <FaArrowLeft className="mr-3 text-md" />
          <span className="text-lg">Back to Room Management</span>
        </Link>

        <Link to={'/dashboard/mdm/room-management/rooms/addroomtype/existing-roomtype'}>
          <button className="bg-black text-white px-4 py-1 mr-6 rounded-md hover:bg-black">
            Edit Existing Room
          </button>
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6 mb-8">
        <div className="grid grid-cols-2 gap-4">
          {/* Room Type Name */}
          <div className="flex flex-col space-y-2">
            <label className="font-semibold">Room Type Name</label>
            <input
              type="text"
              placeholder="Enter room type"
              value={roomType}
              onChange={(e) => setRoomType(e.target.value)}
              className="border border-gray-300 rounded-lg p-2"
              required
            />
          </div>

          {/* Base Price */}
          <div className="flex flex-col space-y-2">
            <label className="font-semibold">Base Price</label>
            <input
              type="number"
              placeholder="Base Price"
              value={basePrice}
              onChange={(e) => setBasePrice(e.target.value)}
              className="border border-gray-300 rounded-lg p-2"
              required
            />
          </div>

          {/* Description (Full Width) */}
          <div className="col-span-2 flex flex-col space-y-2">
            <label className="font-semibold">Description</label>
            <textarea
              placeholder="Enter room description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="border border-gray-300 rounded-lg p-2 h-24"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Max Occupancy */}
          <div className="flex flex-col space-y-2">
            <label className="font-semibold">Max occupancy</label>
            <input
              type="number"
              placeholder="Enter max occupancy"
              value={maxOccupancy}
              onChange={(e) => setMaxOccupancy(e.target.value)}
              className="border border-gray-300 rounded-lg p-2"
              required
            />
          </div>

          {/* Deposit Amount */}
          <div className="flex flex-col space-y-2">
            <label className="font-semibold">Deposit Amount</label>
            <input
              type="number"
              placeholder="Enter deposit amount"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              className="border border-gray-300 rounded-lg p-2"
              required
            />
          </div>
        </div>

        {/* Reservation Terms + Active Status Toggles */}
        <div className="grid grid-cols-2 gap-4">
          {/* Is Refundable Toggle */}
          <div className="flex flex-col space-y-1">
            <label className="font-semibold">Reservation Terms</label>
            <div className="flex items-center justify-between pt-2">
              <div>
                <span className="font-medium text-gray-700">
                  {isRefundable ? 'Refundable' : 'Non-refundable'}
                </span>
                <p className="text-sm text-gray-500 mt-1">
                  {isRefundable
                    ? 'Reservation for this room type are refundable'
                    : 'Reservation for this room type are non-refundable'}
                </p>
              </div>

              <button
                type="button"
                onClick={toggleRefundable}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                  isRefundable ? 'bg-black' : 'bg-gray-400'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full transition-transform duration-300 ${
                    isRefundable ? 'translate-x-6 bg-white' : 'translate-x-1 bg-gray-50'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Is Active Toggle */}
          <div className="flex flex-col space-y-1">
            <label className="font-semibold">Active Status</label>
            <div className="flex items-center justify-between pt-2">
              <div>
                <span className="font-medium text-gray-700">
                  {isActive ? 'Active' : 'Inactive'}
                </span>
                <p className="text-sm text-gray-500 mt-1">
                  {isActive
                    ? 'This room type is visible and bookable'
                    : 'This room type is hidden from guests'}
                </p>
              </div>

              <button
                type="button"
                onClick={toggleActive}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${
                  isActive ? 'bg-green-600' : 'bg-gray-400'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full transition-transform duration-300 ${
                    isActive ? 'translate-x-6 bg-white' : 'translate-x-1 bg-gray-50'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="font-semibold">Upload Room Images</label>
          <div
            {...getRootProps()}
            className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-blue-400 rounded-lg cursor-pointer hover:bg-blue-50"
          >
            <input {...getInputProps()} />
            <p className="text-gray-500">Drag & drop images here, or click to select files</p>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {images.map((file, index) => (
              <img
                key={index}
                src={URL.createObjectURL(file)}
                alt="preview"
                className="w-16 h-16 object-cover rounded-lg"
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-6 gap-6">
          <div className="col-span-6">
            <h3 className="text-lg font-semibold mb-1">Select Amenities</h3>
            <div className="flex flex-wrap gap-4">
              {amenitiesList.map((amenity) => (
                <div key={amenity.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 mr-1"
                    checked={selectedAmenities.includes(amenity.id)}
                    onChange={() =>
                      handleCheckboxChange(selectedAmenities, setSelectedAmenities, amenity.id)
                    }
                  />
                  <label>{amenity.name}</label>
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-6">
            <h3 className="text-lg font-semibold mb-1">Select Service Categories</h3>
            <div className="flex flex-wrap gap-4">
              {serviceCategoriesList.map((service) => (
                <div key={service.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 mr-1"
                    checked={selectedServiceCategories.includes(service.id)}
                    onChange={() =>
                      handleCheckboxChange(
                        selectedServiceCategories,
                        setSelectedServiceCategories,
                        service.id
                      )
                    }
                  />
                  <label>{service.name}</label>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            type="submit"
            className="px-20 py-2 bg-black hover:bg-black text-white rounded-lg text-md font-semibold"
          >
            Save
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddRoomType;

// import React, { useState, useEffect } from 'react';
// import { useDropzone } from 'react-dropzone';
// import { Link } from 'react-router-dom';
// import { FaArrowLeft } from 'react-icons/fa';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// function AddRoomType() {
//   const [roomType, setRoomType] = useState('');
//   const [description, setDescription] = useState('');
//   const [maxOccupancy, setMaxOccupancy] = useState('');
//   const [depositAmount, setDepositAmount] = useState('');
//   const [isRefundable, setIsRefundable] = useState(true);
//   const [images, setImages] = useState([]);
//   const [selectedAmenities, setSelectedAmenities] = useState([]);
//   const [selectedServiceCategories, setSelectedServiceCategories] = useState([]);
//   const [amenitiesList, setAmenitiesList] = useState([]);
//   const [serviceCategoriesList, setServiceCategoriesList] = useState([]);
//   const [basePrice, setBasePrice] = useState('');

//   // Fetch room types data which includes both amenities and service categories
//   useEffect(() => {
//     fetch(`${BQ_BASE_URL}/bq/api/admin-roomtypes/`)
//       .then((response) => response.json())
//       .then((data) => {
//         // Use the all_amenities and all_roomservices from the API response
//         setAmenitiesList(data.all_amenities || []);
//         setServiceCategoriesList(data.all_roomservices || []);
//       })
//       .catch((error) => {
//         console.error('Error fetching room types data:', error);
//         toast.error('Failed to fetch data. Please try again.');
//       });
//   }, []);

//   const onDrop = (acceptedFiles) => {
//     setImages([...images, ...acceptedFiles]);
//   };

//   const { getRootProps, getInputProps } = useDropzone({
//     onDrop,
//     accept: 'image/*',
//     multiple: true,
//   });

//   const handleCheckboxChange = (list, setList, item) => {
//     setList((prev) => (prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]));
//   };

//   const toggleRefundable = () => {
//     setIsRefundable(!isRefundable);
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     // Prepare the form data
//     const formData = new FormData();

//     // Prepare roomtype_data as JSON string
//     const roomtypeData = {
//       propertyid: 1, // Assuming propertyid is fixed or fetched from somewhere
//       roomtypename: roomType,
//       description: description,
//       baseprice: parseFloat(basePrice),
//       max_occupancy: parseInt(maxOccupancy),
//       deposit_amount: parseFloat(depositAmount),
//       is_refundable: isRefundable,
//       amenity_category_ids: selectedAmenities,
//       roomservice_category_ids: selectedServiceCategories,
//     };

//     // Append roomtype_data as JSON string
//     formData.append('roomtype_data', JSON.stringify(roomtypeData));

//     // Append images
//     images.forEach((image) => {
//       formData.append('images', image);
//     });

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/admin-roomtypes/`, {
//         method: 'POST',
//         headers: {
//           accept: 'application/json',
//         },
//         body: formData,
//       });

//       if (!response.ok) {
//         throw new Error('Network response was not ok');
//       }

//       const result = await response.json();
//       console.log('Room type created successfully:', result);
//       toast.success('Room type created successfully!');
//       // Reset the form
//       setRoomType('');
//       setDescription('');
//       setBasePrice('');
//       setDepositAmount('');
//       setMaxOccupancy('');
//       setIsRefundable(true);
//       setImages([]);
//       setSelectedAmenities([]);
//       setSelectedServiceCategories([]);
//     } catch (error) {
//       console.error('Error creating room type:', error);
//       toast.error('Failed to create room type. Please try again.');
//     }
//   };

//   return (
//     <div className="p-6 bg-gray-100 min-h-screen flex flex-col">
//       <ToastContainer
//         position="top-right"
//         autoClose={5000}
//         hideProgressBar={false}
//         newestOnTop={false}
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//       />
//       <div className="flex items-center justify-between mb-2 mt-4">
//         <Link
//           to="/dashboard/mdm/room-management/rooms"
//           className="flex items-center text-gray-400 hover:text-black"
//         >
//           <FaArrowLeft className="mr-3 text-md" />
//           <span className="text-lg">Back to Room Management</span>
//         </Link>

//         <Link to={'/dashboard/mdm/room-management/rooms/addroomtype/existing-roomtype'}>
//           <button className="bg-black text-white px-4 py-1 mr-6 rounded-md hover:bg-black">
//             Edit Existing Room
//           </button>
//         </Link>
//       </div>

//       <form onSubmit={handleSubmit} className="p-6 space-y-6 mb-8">
//         <div className="grid grid-cols-2 gap-4">
//           {/* Room Type Name */}
//           <div className="flex flex-col space-y-2">
//             <label className="font-semibold">Room Type Name</label>
//             <input
//               type="text"
//               placeholder="Enter room type"
//               value={roomType}
//               onChange={(e) => setRoomType(e.target.value)}
//               className="border border-gray-300 rounded-lg p-2"
//               required
//             />
//           </div>

//           {/* Base Price */}
//           <div className="flex flex-col space-y-2">
//             <label className="font-semibold">Base Price</label>
//             <input
//               type="number"
//               placeholder="Base Price"
//               value={basePrice}
//               onChange={(e) => setBasePrice(e.target.value)}
//               className="border border-gray-300 rounded-lg p-2"
//               required
//             />
//           </div>

//           {/* Description (Full Width) */}
//           <div className="col-span-2 flex flex-col space-y-2">
//             <label className="font-semibold">Description</label>
//             <textarea
//               placeholder="Enter room description"
//               value={description}
//               onChange={(e) => setDescription(e.target.value)}
//               className="border border-gray-300 rounded-lg p-2 h-24"
//               required
//             />
//           </div>
//         </div>

//         <div className="grid grid-cols-2 gap-4">
//           {/* Max Occupancy */}
//           <div className="flex flex-col space-y-2">
//             <label className="font-semibold">Max occupancy</label>
//             <input
//               type="number"
//               placeholder="Enter max occupancy"
//               value={maxOccupancy}
//               onChange={(e) => setMaxOccupancy(e.target.value)}
//               className="border border-gray-300 rounded-lg p-2"
//               required
//             />
//           </div>

//           {/* Deposit Amount */}
//           <div className="flex flex-col space-y-2">
//             <label className="font-semibold">Deposit Amount</label>
//             <input
//               type="number"
//               placeholder="Enter deposit amount"
//               value={depositAmount}
//               onChange={(e) => setDepositAmount(e.target.value)}
//               className="border border-gray-300 rounded-lg p-2"
//               required
//             />
//           </div>
//         </div>

//         {/* Add Is Refundable Field with Toggle */}
//         {/* Add Is Refundable Field with Toggle */}
//         <div className="grid grid-cols-2 gap-4">
//           <div className="flex flex-col space-y-1">
//             <label className="font-semibold">Reservation Terms</label>
//             <div className="flex items-center justify-between pt-2">
//               {/* Text on the left side */}
//               <div>
//                 <span className="font-medium text-gray-700">
//                   {isRefundable ? 'Refundable' : 'Non-refundable'}
//                 </span>
//                 <p className="text-sm text-gray-500 mt-1">
//                   {isRefundable
//                     ? 'Reservation for this room type are refundable'
//                     : 'Reservation for this room type are non-refundable'}
//                 </p>
//               </div>

//               {/* Toggle on the right side */}
//               <button
//                 type="button"
//                 onClick={toggleRefundable}
//                 className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${
//                   isRefundable ? 'bg-black' : 'bg-gray-400'
//                 }`}
//               >
//                 {/* Toggle knob */}
//                 <span
//                   className={`inline-block h-4 w-4 transform rounded-full transition-transform duration-300 ${
//                     isRefundable ? 'translate-x-6 bg-white' : 'translate-x-1 bg-gray-50'
//                   }`}
//                 />
//               </button>
//             </div>
//           </div>
//         </div>

//         <div className="space-y-2">
//           <label className="font-semibold">Upload Room Images</label>
//           <div
//             {...getRootProps()}
//             className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-blue-400 rounded-lg cursor-pointer hover:bg-blue-50"
//           >
//             <input {...getInputProps()} />
//             <p className="text-gray-500">Drag & drop images here, or click to select files</p>
//           </div>
//           <div className="mt-2 flex flex-wrap gap-2">
//             {images.map((file, index) => (
//               <img
//                 key={index}
//                 src={URL.createObjectURL(file)}
//                 alt="preview"
//                 className="w-16 h-16 object-cover rounded-lg"
//               />
//             ))}
//           </div>
//         </div>

//         <div className="grid grid-cols-6 gap-6">
//           <div className="col-span-6">
//             <h3 className="text-lg font-semibold mb-1">Select Amenities</h3>
//             <div className="flex flex-wrap gap-4">
//               {amenitiesList.map((amenity) => (
//                 <div key={amenity.id} className="flex items-center space-x-2">
//                   <input
//                     type="checkbox"
//                     className="h-4 w-4 mr-1"
//                     checked={selectedAmenities.includes(amenity.id)}
//                     onChange={() =>
//                       handleCheckboxChange(selectedAmenities, setSelectedAmenities, amenity.id)
//                     }
//                   />
//                   <label>{amenity.name}</label>
//                 </div>
//               ))}
//             </div>
//           </div>

//           <div className="col-span-6">
//             <h3 className="text-lg font-semibold mb-1">Select Service Categories</h3>
//             <div className="flex flex-wrap gap-4">
//               {serviceCategoriesList.map((service) => (
//                 <div key={service.id} className="flex items-center space-x-2">
//                   <input
//                     type="checkbox"
//                     className="h-4 w-4 mr-1"
//                     checked={selectedServiceCategories.includes(service.id)}
//                     onChange={() =>
//                       handleCheckboxChange(
//                         selectedServiceCategories,
//                         setSelectedServiceCategories,
//                         service.id
//                       )
//                     }
//                   />
//                   <label>{service.name}</label>
//                 </div>
//               ))}
//             </div>
//           </div>
//         </div>

//         <div className="flex justify-end mt-4">
//           <button
//             type="submit"
//             className="px-20 py-2 bg-black hover:bg-black text-white rounded-lg text-md font-semibold"
//           >
//             Save
//           </button>
//         </div>
//       </form>
//     </div>
//   );
// }

// export default AddRoomType;
